import { Box, Container, Typography, CircularProgress, Button, Stack, Paper } from '@mui/material';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';

type VerificationStatus = 'loading' | 'verifying' | 'success' | 'error' | 'expired' | 'already_completed';

export function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const redirectTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided');
      return;
    }

    let cancelled = false;

    const verifyAndActivate = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';

        const checkResponse = await fetch(`${apiUrl}/auth/signup/${token}`);
        const checkResult = await checkResponse.json();

        if (cancelled) return;

        if (!checkResponse.ok) {
          if (checkResult.code === 'TOKEN_EXPIRED') {
            setStatus('expired');
            setMessage('This verification link has expired. Please request a new one.');
            return;
          }
          if (checkResult.code === 'ALREADY_COMPLETED') {
            setStatus('already_completed');
            setMessage('Your account is already active.');
            return;
          }
          throw new Error(checkResult.message || 'Invalid token');
        }

        if (checkResult.data.status === 'completed') {
          setStatus('already_completed');
          setMessage('Your account is already active.');
          return;
        }

        setStatus('verifying');
        const completeResponse = await fetch(`${apiUrl}/auth/signup/${token}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        const completeResult = await completeResponse.json();

        if (cancelled) return;

        if (!completeResponse.ok) {
          throw new Error(completeResult.message || 'Failed to activate account');
        }

        if (completeResult.data.sessionToken) {
          localStorage.setItem('rosta_token', completeResult.data.sessionToken);
        }

        setStatus('success');
        setMessage('Your account has been activated successfully!');

        redirectTimer.current = setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (err: unknown) {
        if (cancelled) return;
        const errorMessage = err instanceof Error ? err.message : 'An error occurred during verification';
        setStatus('error');
        setMessage(errorMessage);
      }
    };

    const timer = setTimeout(verifyAndActivate, 1000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (redirectTimer.current) clearTimeout(redirectTimer.current as ReturnType<typeof setTimeout>);
    };
  }, [token, navigate]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#050510' }}>
      <Container maxWidth="sm">
        <Paper
          sx={{
            p: 5,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.06)',
            backgroundColor: 'rgba(18, 18, 31, 0.8)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {status === 'loading' && (
            <Stack spacing={3} alignItems="center">
              <CircularProgress sx={{ color: '#3d7aff' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0f0f5' }}>
                Verifying your email...
              </Typography>
              <Typography sx={{ color: '#a0a5b5' }}>
                Please wait while we verify your email address.
              </Typography>
            </Stack>
          )}

          {status === 'verifying' && (
            <Stack spacing={3} alignItems="center">
              <CircularProgress sx={{ color: '#22c55e' }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0f0f5' }}>
                Activating your account...
              </Typography>
              <Typography sx={{ color: '#a0a5b5' }}>
                Setting up your organization and creating your profile.
              </Typography>
            </Stack>
          )}

          {status === 'success' && (
            <Stack spacing={3} alignItems="center">
              <Box sx={{ fontSize: 64, lineHeight: 1 }}>✓</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#22c55e' }}>
                Email Verified!
              </Typography>
              <Typography sx={{ color: '#a0a5b5' }}>
                Your account has been activated. Redirecting to dashboard...
              </Typography>
              <CircularProgress size={24} sx={{ color: '#3d7aff' }} />
            </Stack>
          )}

          {status === 'error' && (
            <Stack spacing={3} alignItems="center">
              <Box sx={{ fontSize: 64, lineHeight: 1, color: '#ef4444' }}>✕</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0f0f5' }}>
                Verification Failed
              </Typography>
              <Typography sx={{ color: '#a0a5b5' }}>
                {message}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  component={Link}
                  to="/signup"
                  variant="outlined"
                  sx={{ borderColor: 'rgba(255,255,255,0.15)', color: '#a0a5b5', textTransform: 'none' }}
                >
                  Sign Up Again
                </Button>
                <Button
                  component={Link}
                  to="/sign-in"
                  variant="contained"
                  sx={{ background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)', textTransform: 'none' }}
                >
                  Sign In
                </Button>
              </Stack>
            </Stack>
          )}

          {status === 'expired' && (
            <Stack spacing={3} alignItems="center">
              <Box sx={{ fontSize: 64, lineHeight: 1, color: '#f59e0b' }}>⏰</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0f0f5' }}>
                Link Expired
              </Typography>
              <Typography sx={{ color: '#a0a5b5' }}>
                {message}
              </Typography>
              <Button
                component={Link}
                to="/signup"
                variant="contained"
                sx={{ background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)', textTransform: 'none' }}
              >
                Sign Up Again
              </Button>
            </Stack>
          )}

          {status === 'already_completed' && (
            <Stack spacing={3} alignItems="center">
              <Box sx={{ fontSize: 64, lineHeight: 1, color: '#22c55e' }}>✓</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0f0f5' }}>
                Already Verified
              </Typography>
              <Typography sx={{ color: '#a0a5b5' }}>
                {message}
              </Typography>
              <Button
                component={Link}
                to="/sign-in"
                variant="contained"
                sx={{ background: 'linear-gradient(135deg, #1a4cd8, #3d7aff)', textTransform: 'none' }}
              >
                Go to Sign In
              </Button>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
